import { ref } from 'vue'
import { defineStore } from 'pinia'
import { http, isUpgradeRequiredError } from '@/helpers/http'
import { requiredPlanForBookFormat } from '@/helpers/product'
import { useUpgradeStore } from '@/stores/upgrade'
import type {
  TCreatePresentationFromSlidesPayload,
  TCreatePresentationFromSlidesResponse,
  TDraftOutlinePoint,
  TPresentationBackground,
  TPresentationGenerationJob,
  TPresentation,
  TPresentationDocument,
  TPresentationExport,
  TPresentationListItem,
  TPresentationOutlineSection,
  TPresentationsResult,
} from '@/types/presentation'

type TPresentationSavePayload = {
  id: number | null
  draft_id: number | null
  content: TPresentationDocument
  thumbnail?: string | null
}

type TCreatePresentationFromDraftPayload = {
  draftId: number
  content?: string
  title: string
  slidesCount: number
  includeNotes: boolean
  noteVoiceId: string | null
  useOutlineDetection?: boolean
  selectedOutlinePointIndices?: number[]
  themeId?: string
  themeBackgrounds?: Record<string, TPresentationBackground>
  language?: string
  transitionType?: 'none' | 'fade'
  defaultSlideDurationMs?: number
  minSlideDurationMs?: number
}

type TMonthlyLimitBlock = {
  message?: string
  monthly_books_limit?: number
  monthly_books_used?: number
  plan_name?: string
}

type TCreatePresentationFromDraftResponse = TMonthlyLimitBlock & {
  result: boolean
  error?: string
  job: TPresentationGenerationJob | null
}

type TPreviewDraftOutlinePayload = {
  draftId: number
  content?: string
  slidesCount?: number
}

type TPreviewDraftOutlineResponse = {
  result: boolean
  error?: string
  source?: string
  points?: TDraftOutlinePoint[]
  slideDeckImport?: boolean
}

type TCreatePresentationFromOutlinePayload = {
  title: string
  language: string
  sections: TPresentationOutlineSection[]
  slidesMin?: number
  slidesMax?: number
  includeNotes: boolean
  noteVoiceId: string | null
  themeId?: string
  themeBackgrounds?: Record<string, TPresentationBackground>
  transitionType?: 'none' | 'fade'
  defaultSlideDurationMs?: number
  minSlideDurationMs?: number
}

type TCreatePresentationFromOutlineResponse = TMonthlyLimitBlock & {
  result: boolean
  error?: string
  job: TPresentationGenerationJob | null
}

type TPresentationGenerationStatusResponse = {
  result: boolean
  job: TPresentationGenerationJob | null
  presentation: TPresentation | null
}

type TPresentationGenerationProcessResponse = {
  result: boolean
  message?: string
  error?: string
}

export const usePresentationsStore = defineStore('presentations', () => {
  const presentations = ref<TPresentationListItem[]>([])
  const total = ref(0)
  const listPage = ref(1)
  const listSortBy = ref('latest')
  const loading = ref(false)
  const idsWithoutThumbnails = ref<number[]>([])
  const generateRunning = ref(false)
  const generateTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const loaded = ref(false)
  let pendingLoad: Promise<void> | null = null

  function invalidate() {
    loaded.value = false
  }

  function collectIdsWithoutThumbnails() {
    idsWithoutThumbnails.value = presentations.value.filter((item) => !item.thumbnail).map((item) => item.id)
  }

  function startThumbnailGeneration() {
    generateRunning.value = true

    if (generateTimeout.value) clearTimeout(generateTimeout.value)

    checkThumbnail()
  }

  async function fetch(nextPage = false, sortBy = '', query = '') {
    if (!nextPage) listPage.value = 1
    if (sortBy) listSortBy.value = sortBy

    loading.value = true

    try {
      const suffix = query ? `?query=${encodeURIComponent(query)}` : ''
      const data = await http.get<TPresentationsResult>(`/presentations/${listPage.value}/${listSortBy.value}${suffix}`)

      presentations.value = nextPage ? presentations.value.concat(data.results) : data.results
      total.value = data.pagination.total
      loaded.value = query === ''

      collectIdsWithoutThumbnails()

      if (!nextPage) {
        startThumbnailGeneration()
      }
    } catch {
      console.error('Failed to fetch presentations')
    } finally {
      loading.value = false
    }
  }

  async function ensurePresentations() {
    if (loaded.value) {
      collectIdsWithoutThumbnails()
      startThumbnailGeneration()
      return
    }
    if (pendingLoad) {
      return pendingLoad
    }

    pendingLoad = fetch()
    try {
      await pendingLoad
    } finally {
      pendingLoad = null
    }
  }

  async function checkThumbnail() {
    const id = idsWithoutThumbnails.value.length > 0 ? idsWithoutThumbnails.value[0] : null

    if (!id) {
      generateRunning.value = false

      return
    }

    idsWithoutThumbnails.value.shift()

    if (!generateRunning.value) return

    await generateThumbnailById(id)

    generateTimeout.value = setTimeout(() => checkThumbnail(), 1000)
  }

  async function generateThumbnailById(id: number) {
    try {
      const res = await http.get<{ result: boolean; id: number; thumbnail: string }>(
        `/presentations/generate-thumbnail/${id}`,
      )

      if (!res?.result) return

      const item = presentations.value.find((presentation) => presentation.id === id)

      if (item) item.thumbnail = res.thumbnail
    } catch {
      console.error('Failed to generate presentation thumbnail')
    }
  }

  async function fetchNext() {
    listPage.value++
    await fetch(true)
  }

  async function get(id: number, token?: string) {
    try {
      const suffix = token ? `/${token}` : ''
      return await http.get<TPresentation>(`/presentations/item/${id}${suffix}`)
    } catch {
      console.error('Failed to fetch presentation')

      return null
    }
  }

  async function getFlipbookUrl(id: number) {
    try {
      const res = await http.get<{ result: boolean; url: string }>(`/presentations/flipbook-url/${id}`)

      return res?.result ? res.url : null
    } catch {
      console.error('Failed to fetch the flipbook share URL')

      return null
    }
  }

  async function getShared(hash: string) {
    try {
      return await http.get<TPresentation>(`/presentations/shared/${hash}`)
    } catch {
      console.error('Failed to fetch shared presentation')

      return null
    }
  }

  async function getSharedByToken(id: string, token: string) {
    try {
      return await http.get<TPresentation>(`/presentations/shared/${id}/${token}`)
    } catch {
      console.error('Failed to fetch shared presentation')

      return null
    }
  }

  async function share(id: number) {
    try {
      const res = await http.post<{ result: boolean; url: string }>(`/presentations/share/${id}`, {})

      return res.result ? res.url : null
    } catch {
      console.error('Failed to create share link')

      return null
    }
  }

  async function unshare(id: number) {
    try {
      const res = await http.post<{ result: boolean }>(`/presentations/unshare/${id}`, {})

      return res.result
    } catch {
      console.error('Failed to disable sharing')

      return false
    }
  }

  function showUpgradeIfCreationRefused(e: unknown) {
    if (isUpgradeRequiredError(e)) {
      useUpgradeStore().showUpgradeModal('presentation-create', requiredPlanForBookFormat('presentation'))
    }
  }

  async function save(payload: TPresentationSavePayload) {
    try {
      const res = await http.post<{ result: boolean; id: number }>('/presentations/item', payload)

      if (res.result) invalidate()

      return res.result ? res.id : null
    } catch (e) {
      showUpgradeIfCreationRefused(e)
      console.error('Failed to save presentation')

      return null
    }
  }

  async function remove(id: number) {
    try {
      const res = await http.delete<{ result: boolean }>(`/presentations/item/${id}`)

      if (res.result) fetch()

      return res.result
    } catch {
      console.error('Failed to remove presentation')

      return false
    }
  }

  async function removeMany(ids: number[]) {
    try {
      const res = await http.post<{ result: boolean }>('/presentations/delete-many', { ids })

      if (res.result) fetch()

      return res.result
    } catch {
      console.error('Failed to remove presentations')

      return false
    }
  }

  async function rename(id: number, title: string) {
    try {
      await http.post('/presentations/rename', { id, title }, true)

      return true
    } catch {
      return false
    }
  }

  async function duplicate(id: number) {
    try {
      const res = await http.post<{ result: boolean; id: number }>('/presentations/duplicate', { id })

      if (res.result) fetch()

      return res.result ? res.id : null
    } catch (e) {
      showUpgradeIfCreationRefused(e)
      console.error('Failed to duplicate presentation')

      return null
    }
  }

  async function postCreation<T>(
    url: string,
    payload:
      | TCreatePresentationFromDraftPayload
      | TPreviewDraftOutlinePayload
      | TCreatePresentationFromOutlinePayload
      | TCreatePresentationFromSlidesPayload,
  ): Promise<T> {
    try {
      return await http.post<T>(url, payload)
    } catch (e) {
      showUpgradeIfCreationRefused(e)

      throw e
    }
  }

  async function createFromDraft(payload: TCreatePresentationFromDraftPayload) {
    const res = await postCreation<TCreatePresentationFromDraftResponse>(
      '/presentations/create-presentation-from-draft',
      payload,
    )

    if (res.result) invalidate()

    return res
  }

  async function createFromSlides(payload: TCreatePresentationFromSlidesPayload) {
    const res = await postCreation<TCreatePresentationFromSlidesResponse>(
      '/presentations/create-presentation-from-slides',
      payload,
    )

    if (res.result) invalidate()

    return res
  }

  async function previewDraftOutline(payload: TPreviewDraftOutlinePayload) {
    return postCreation<TPreviewDraftOutlineResponse>('/presentations/preview-draft-outline', payload)
  }

  async function createFromOutline(payload: TCreatePresentationFromOutlinePayload) {
    const res = await postCreation<TCreatePresentationFromOutlineResponse>(
      '/presentations/create-presentation-from-outline',
      payload,
    )

    if (res.result) invalidate()

    return res
  }

  async function getActiveExports() {
    try {
      const res = await http.get<{ result: boolean; exports: TPresentationExport[] }>('/presentations/exports/active')

      return res.result ? (res.exports ?? []) : []
    } catch {
      console.error('Failed to fetch active presentation exports')

      return []
    }
  }

  async function getGenerationStatus(jobId: number) {
    try {
      return await http.get<TPresentationGenerationStatusResponse>(`/presentations/generation/status/${jobId}`)
    } catch {
      console.error('Failed to fetch presentation generation status')

      return null
    }
  }

  async function processGenerationJob(jobId: number) {
    try {
      return await http.get<TPresentationGenerationProcessResponse>(`/presentations/generation/process/${jobId}`)
    } catch {
      console.error('Failed to process presentation generation job')

      return null
    }
  }

  async function queueVideoExport(presentationId: number) {
    try {
      const res = await http.post<{ result: boolean; export: TPresentationExport | null }>(
        '/presentations/export/video/queue',
        { presentation_id: presentationId },
      )

      return res.result ? (res.export ?? null) : null
    } catch {
      console.error('Failed to queue presentation video export')

      return null
    }
  }

  async function getVideoExportStatus(presentationId: number) {
    try {
      const res = await http.get<{ result: boolean; export: TPresentationExport | null }>(
        `/presentations/export/video/status/${presentationId}`,
      )

      return res.result ? (res.export ?? null) : null
    } catch {
      console.error('Failed to fetch presentation video export status')

      return null
    }
  }

  async function queuePngExport(presentationId: number) {
    try {
      const res = await http.post<{ result: boolean; export: TPresentationExport | null }>(
        '/presentations/export/png/queue',
        { presentation_id: presentationId },
      )

      return res.result ? (res.export ?? null) : null
    } catch {
      console.error('Failed to queue presentation PNG export')

      return null
    }
  }

  async function getExportStatus(presentationId: number) {
    try {
      const res = await http.get<{ result: boolean; export: TPresentationExport | null }>(
        `/presentations/export/png/status/${presentationId}`,
      )

      return res.result ? (res.export ?? null) : null
    } catch {
      console.error('Failed to fetch presentation PNG export status')

      return null
    }
  }

  async function queuePdfExport(presentationId: number) {
    try {
      const res = await http.post<{ result: boolean; export: TPresentationExport | null }>(
        '/presentations/export/pdf/queue',
        { presentation_id: presentationId },
      )

      return res.result ? (res.export ?? null) : null
    } catch {
      console.error('Failed to queue presentation PDF export')

      return null
    }
  }

  async function getPdfExportStatus(presentationId: number) {
    try {
      const res = await http.get<{ result: boolean; export: TPresentationExport | null }>(
        `/presentations/export/pdf/status/${presentationId}`,
      )

      return res.result ? (res.export ?? null) : null
    } catch {
      console.error('Failed to fetch presentation PDF export status')

      return null
    }
  }

  return {
    presentations,
    total,
    listPage,
    listSortBy,
    loading,
    fetch,
    ensurePresentations,
    invalidate,
    fetchNext,
    get,
    getFlipbookUrl,
    getShared,
    getSharedByToken,
    share,
    unshare,
    save,
    remove,
    removeMany,
    rename,
    duplicate,
    createFromDraft,
    createFromSlides,
    createFromOutline,
    previewDraftOutline,
    getActiveExports,
    getGenerationStatus,
    processGenerationJob,
    queueVideoExport,
    getVideoExportStatus,
    queuePngExport,
    getExportStatus,
    queuePdfExport,
    getPdfExportStatus,
    generateThumbnailById,
  }
})
